import { useCallback } from 'react';
import { useParams } from 'react-router-dom';

import { EmptyState } from '@components/empty-state';
import { IconJourneyItem } from '@components/icons/journey-icon';
import {
  IntegrationDetail as CoreIntegrationDetail,
  OpenAPIImportHandlers,
  setConditionHelpers,
  setWebhookLabelHelpers,
  useIntegration,
} from '@components/integrations';
import { Spinner } from '@components/loading-spinner';
import { PageContent } from '@components/page-containers/page-container';
import { Stack } from '@components/stack';
import { useScopedTranslate } from '@locale/translate';

import { useUserOption } from 'api/authentication/hooks';
import {
  useCreateEndpointsFromOpenAPI,
  useIntegrationJourneys,
  useParseOpenAPISpec,
} from 'api/integrations/hooks';
import { JourneyType } from 'api/types';
import { getComparatorLabel, getValueLabel } from 'ui/actions/condition-v2/description';
import { ConditionComponent, getComparatorIds } from 'ui/actions/condition-v2/helpers';
import { JourneyListItem } from 'ui/authenticated/main/pages/journeys/journey-item';
import { routePaths } from 'ui/authenticated/navigation';
import { useFeature } from 'ui/hooks';

setConditionHelpers({
  ConditionComponent,
  getComparatorIds,
});

setWebhookLabelHelpers({
  getComparatorLabel,
  getValueLabel,
});

const JourneysTabContent = () => {
  const { t } = useScopedTranslate(locale);
  const { integrationId } = useParams<{ integrationId: string }>();
  const journeysQuery = useIntegrationJourneys(integrationId!);

  if (!journeysQuery.data) {
    return <Spinner />;
  }

  return (
    <PageContent>
      <Stack space={2}>
        {journeysQuery.data.length === 0 ? (
          <EmptyState
            skipMargin
            icon={<IconJourneyItem size={80} />}
            header={t('noJourneys')}
            content={t('noJourneysDescription')}
          />
        ) : (
          <Stack space={2}>
            {journeysQuery.data.map((journey) => (
              <div key={journey.shortId}>
                <JourneyListItem journey={journey as JourneyType} hideMenu />
              </div>
            ))}
          </Stack>
        )}
      </Stack>
    </PageContent>
  );
};

export const IntegrationDetail = () => {
  const { t } = useScopedTranslate({
    en: {
      journeys: 'Journeys',
    },
  });
  const { integrationId } = useParams<{ integrationId: string }>();
  const { isEnabled: hasWebhooks } = useFeature('FEATURE_INTEGRATION_WEBHOOKS');
  const { value: hasHidden, update } = useUserOption('hasHiddenIntegrationWizard', false);
  const integrationQuery = useIntegration(integrationId!);

  const parseMutation = useParseOpenAPISpec(integrationId!);
  const createMutation = useCreateEndpointsFromOpenAPI();

  const onParse = useCallback(
    async (file: File) => {
      return parseMutation.mutateAsync(file);
    },
    [parseMutation]
  );

  const onCreate = useCallback(
    async (params: Parameters<OpenAPIImportHandlers['onCreate']>[0]) => {
      return createMutation.mutateAsync({
        integrationShortId: integrationId!,
        endpoints: params.endpoints,
        updateBaseUrl: params.updateBaseUrl,
        baseUrl: params.baseUrl,
      });
    },
    [createMutation, integrationId]
  );

  if (!integrationQuery.data) {
    return <Spinner />;
  }

  const journeysTab = {
    content: <JourneysTabContent />,
    counter: integrationQuery.data.numJourneys,
    label: t('journeys'),
    section: 'journeys',
  };

  const openAPIImport: OpenAPIImportHandlers = {
    onParse,
    onCreate,
  };

  return (
    <CoreIntegrationDetail
      backUrl={routePaths.integrations}
      extraTabs={[journeysTab]}
      hasWebhooks={hasWebhooks}
      onDismissSetupHelper={() => update.mutate(true)}
      hasHiddenSetupHelper={hasHidden}
      openAPIImport={openAPIImport}
    />
  );
};

const locale = {
  en: {
    noJourneys: 'No journeys',
    noJourneysDescription: 'This integration is currently not used in any journeys',
  },
};
